import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useTranslation } from "@/i18n";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";
import { START_OF_WEEK_FLAG } from "@/lib/launchdarkly/flags";
import { useStore } from "@/stores/store-context";

/**
 * Start of week picker for the preferences page. Renders nothing while the
 * `start-of-week` flag is off: no view reads the saved value yet (NG-2275).
 */
export const StartOfWeekPreference = observer(function StartOfWeekPreference() {
  const enabled = useFeatureFlag(START_OF_WEEK_FLAG);
  return enabled ? <StartOfWeekField /> : null;
});

const StartOfWeekField = observer(function StartOfWeekField() {
  const { t } = useTranslation();
  const { user } = useStore();
  const saved = user.profile?.start_of_the_week ?? 0;

  const [startOfWeek, setStartOfWeek] = useState<number>(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStartOfWeek(saved);
  }, [saved]);

  const handleChange = async (next: number) => {
    setStartOfWeek(next);
    setSaving(true);
    try {
      await user.updateProfile({ start_of_the_week: next });
      toast.success(t("profile_settings.preferences.save_success"));
    } catch (error) {
      console.error("[profile] preference save failed", requestErrorSummary(error));
      toast.error(t("profile_settings.preferences.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="preferences-start-of-week">
        {t("profile_settings.preferences.start_of_week_label")}
      </Label>
      <Select
        value={String(startOfWeek)}
        onValueChange={(v) => v && void handleChange(Number(v))}
        disabled={saving}
      >
        <SelectTrigger id="preferences-start-of-week" className="w-full">
          <SelectValue>
            {(value) =>
              value === "1"
                ? t("profile_settings.preferences.start_of_week_monday")
                : t("profile_settings.preferences.start_of_week_sunday")
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">
            {t("profile_settings.preferences.start_of_week_sunday")}
          </SelectItem>
          <SelectItem value="1">
            {t("profile_settings.preferences.start_of_week_monday")}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
});
